// Shared binder constants + validation, kept out of the server-fn wrapper file.
import { z } from "zod";

/** A binder page is a classic 3×3 nine-pocket sheet. */
export const BINDER_SLOTS_PER_PAGE = 9;
/** How many pages a Pal Stars binder holds (9 pockets × 12 pages = 108 cards). */
export const BINDER_PAGES = 12;
export const BINDER_CAPACITY = BINDER_SLOTS_PER_PAGE * BINDER_PAGES;

export type PullStatus = "new" | "binder" | "trade" | "discarded";

export const setStatusSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["new", "binder", "trade", "discarded"]),
  /** Required for `binder`; the first free pocket is used when omitted. */
  slot: z.number().int().min(0).max(BINDER_CAPACITY - 1).nullable().default(null),
});

export const binderOwnerSchema = z.object({ user_id: z.string().uuid() });

/** Moving one card into an exact pocket, swapping with whatever sits there. */
export const placeSlotSchema = z.object({
  id: z.string().uuid(),
  slot: z.number().int().min(0).max(BINDER_CAPACITY - 1),
});

/** Star / unstar one pulled card. */
export const favoriteSchema = z.object({
  id: z.string().uuid(),
  favorite: z.boolean(),
});


export interface BinderSection {
  id: string;
  label: string;
  /** Color used for the tab and page trim. */
  tint: string;
  /** Zero-based page indexes that belong to this section. */
  pages: number[];
  blurb: string;
}

/**
 * The binder is organised the way collectors sort a Palworld deck box: one
 * color block per pair of nine-pocket pages, then a dedicated chase section at
 * the back for SSP, OSR and SSS Ultra Secret Gold cards.
 */
export const BINDER_SECTIONS: BinderSection[] = [
  { id: "Red", label: "Red collection", tint: "#ef4444", pages: [0, 1], blurb: "Fire and fury — every red Palworld card." },
  { id: "Blue", label: "Blue collection", tint: "#38bdf8", pages: [2, 3], blurb: "Water, ice and the blue support core." },
  { id: "Green", label: "Green collection", tint: "#22c55e", pages: [4, 5], blurb: "Grass, ground and the green work crew." },
  { id: "Purple", label: "Purple collection", tint: "#a855f7", pages: [6, 7], blurb: "Dark, dragon and purple tempo cards." },
  { id: "Colorless", label: "Colorless & gear", tint: "#94a3b8", pages: [8, 9], blurb: "Gear, structures, events, Souls and Counters." },
  { id: "Chase", label: "Chase page — SSP · OSR · SSS", tint: "#fbbf24", pages: [10, 11], blurb: "Ultra Secret Gold, Over Super Rare and SSP hits only." },
];

export const CHASE_RARITIES = ["SSP", "OSR", "SSS"];

/** Which section a card belongs in: chase rarity wins over card color. */
export function sectionForCard(rarity: string, color?: string): BinderSection {
  if (CHASE_RARITIES.includes(rarity)) return BINDER_SECTIONS[5];
  return BINDER_SECTIONS.find((s) => s.id === color) ?? BINDER_SECTIONS[4];
}

export function sectionForSlot(slot: number): BinderSection {
  const page = Math.floor(slot / BINDER_SLOTS_PER_PAGE);
  return BINDER_SECTIONS.find((s) => s.pages.includes(page)) ?? BINDER_SECTIONS[4];
}

export function slotsOfSection(section: BinderSection): number[] {
  return section.pages.flatMap((p) =>
    Array.from({ length: BINDER_SLOTS_PER_PAGE }, (_, i) => p * BINDER_SLOTS_PER_PAGE + i),
  );
}

/** Secret binder share links: opaque base64url tokens, revocable and expiring. */
export const shareTokenSchema = z.object({ token: z.string().min(24).max(120).regex(/^[A-Za-z0-9_-]+$/) });
export const shareLabelSchema = z.object({ label: z.string().max(60).default("Binder share link") });
export const revokeTokenSchema = z.object({ id: z.string().uuid() });

/** A card as returned by the public tokenized binder read. */
export interface SharedBinderCard {
  id: string;
  card_code: string;
  card_name: string;
  rarity: string;
  set_code: string;
  image: string;
  grade: number;
  grade_label: string;
  score: number;
  status: string;
  binder_slot: number | null;
  pulled_at: string;
}
